import { createElement } from '@wordpress/element';

const BadgeList = ({ badges, onEdit, onDelete, onNew }) => {
    const assetsUrl = window.PBFW_SETTINGS?.assetsUrl || '';

    // Derive a small visual hint for each badge
    const getPreview = (badge) => {
        const bg    = badge.pbfw_bg_clr    || '#6366f1';
        const clr   = badge.pbfw_font_clr  || '#fff';
        const txt   = (badge.pbfw_discount_text || 'Badge').replace(/<[^>]*>/g, '');
        const shape = badge.pbfw_lbl_shape || 'square';
        const pos   = badge.pbfw_image_position || 'top_left';
        const fw    = badge.pbfw_font_weight || 'bold';
    
        if (badge.badge_define === 'or_image_badge' && badge.image_badge) {
            return <img src={`${assetsUrl}/img/${badge.image_badge}`} style={{ width: 36, height: 36, objectFit: 'contain' }} alt="" />;
        }
    
        const offersClip = 'polygon(50% 0%,60% 13%,75% 7%,77% 23%,93% 25%,87% 40%,100% 50%,87% 60%,93% 75%,77% 77%,75% 93%,60% 87%,50% 100%,40% 87%,25% 93%,23% 77%,7% 75%,13% 60%,0% 50%,13% 40%,7% 25%,23% 23%,25% 7%,40% 13%)';
        const tagClip    = 'polygon(0% 0%,100% 0%,100% 100%,50% 75%,0% 100%)';
        const cornerClips = {
            top_left:     'polygon(0% 0%, 100% 0%, 0% 100%)',
            top_right:    'polygon(0% 0%, 100% 0%, 100% 100%)',
            bottom_left:  'polygon(0% 0%, 0% 100%, 100% 100%)',
            bottom_right: 'polygon(100% 0%, 0% 100%, 100% 100%)',
        };
    
        const base = {
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: bg, color: clr, fontSize: 9, fontWeight: fw,
            boxSizing: 'border-box', textAlign: 'center', lineHeight: 1.2,
            overflow: 'hidden', wordBreak: 'break-word', flexShrink: 0,
        };
    
        // Collar — rendered as two separate elements
        if (shape === 'collar') {
            return (
                <div style={{ position: 'relative', width: 36, height: 46, flexShrink: 0 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: bg, color: clr,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: fw, textAlign: 'center',
                        padding: 3, boxSizing: 'border-box',
                    }}>{txt}</div>
                    <div style={{
                        position: 'absolute', bottom: 2, left: 4,
                        width: 16, height: 12, background: bg,
                        clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)',
                        transform: 'rotate(32deg)', transformOrigin: 'top center',
                    }} />
                    <div style={{
                        position: 'absolute', bottom: 2, right: 4,
                        width: 16, height: 12, background: bg,
                        clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)',
                        transform: 'rotate(-32deg)', transformOrigin: 'top center',
                    }} />
                </div>
            );
        }
    
        // Corner badge — triangle with rotated text
        if (shape === 'corner_badge') {
            let clipKey = pos;
            if (pos === 'custom_position') {
                const isRight  = parseFloat(badge.pbfw_right)  > 0;
                const isBottom = parseFloat(badge.pbfw_bottom) > 0;
                if      ( isBottom &&  isRight) clipKey = 'bottom_right';
                else if ( isBottom && !isRight) clipKey = 'bottom_left';
                else if (!isBottom &&  isRight) clipKey = 'top_right';
                else                            clipKey = 'top_left';
            }
            const clip  = cornerClips[clipKey] || cornerClips.top_left;
            const rot   = { top_left: 'rotate(-45deg)', top_right: 'rotate(45deg)', bottom_left: 'rotate(45deg)', bottom_right: 'rotate(-45deg)' }[clipKey] || 'rotate(-45deg)';
            const tTop  = (clipKey === 'top_left' || clipKey === 'top_right')   ? '35%' : '65%';
            const tLeft = (clipKey === 'top_left' || clipKey === 'bottom_left') ? '35%' : '65%';
            return (
                <div style={{ position: 'relative', width: 44, height: 44, background: bg, clipPath: clip, flexShrink: 0 }}>
                    <span style={{
                        position: 'absolute', top: tTop, left: tLeft,
                        transform: `translate(-50%,-50%) ${rot}`,
                        color: clr, fontSize: 8, fontWeight: fw,
                        textAlign: 'center', width: '70%', display: 'block', lineHeight: 1.2,
                    }}>{txt}</span>
                </div>
            );
        }
    
        // Shape style map
        const shapeStyles = {
            square:           { width: 36, height: 36, borderRadius: 2 },
            rectangle:        { width: 56, height: 26, borderRadius: 0 },
            rectangle_up:     { width: 30, height: 30, borderRadius: 3, transform: 'rotate(45deg)', opacity: 0.85 },
            offers:           { width: 40, height: 40, borderRadius: 0, clipPath: offersClip },
            tag:              { width: 26, height: 38, clipPath: tagClip, alignItems: 'flex-start', paddingTop: 4 },
            rectangle_round:  { width: 56, height: 26, borderRadius: 8 },
            rectangle_circle: { width: 56, height: 26, borderRadius: '50%' },
            circle:           { width: 36, height: 36, borderRadius: '50%' },
        };
    
        const shapeStyle = shapeStyles[shape] || shapeStyles.square;
    
        // Diamond text counter-rotates
        const spanStyle = shape === 'rectangle_up'
            ? { transform: 'rotate(-45deg)', display: 'inline-block', width: '100%', textAlign: 'center' }
            : {};
    
        return (
            <div style={{ ...base, ...shapeStyle, padding: shape === 'tag' ? '4px 4px 12px' : '3px 4px' }}>
                <span style={spanStyle}>{txt}</span>
            </div>
        );
    };

    return (
        <div className="pbfw-list-page">
            {/* Stats */}
            <div className="cpiw-stats-row">
                <div className="cpiw-stat-card">
                    <div className="cpiw-stat-card__icon cpiw-stat-card__icon--brand">🏷️</div>
                    <div><div className="cpiw-stat-card__value">{badges.length}</div><div className="cpiw-stat-card__label">Total Badges</div></div>
                </div>
                <div className="cpiw-stat-card">
                    <div className="cpiw-stat-card__icon cpiw-stat-card__icon--accent">✅</div>
                    <div><div className="cpiw-stat-card__value">{badges.filter(b => b.pbfw_show_label === 'on').length}</div><div className="cpiw-stat-card__label">Active</div></div>
                </div>
                <div className="cpiw-stat-card">
                    <div className="cpiw-stat-card__icon cpiw-stat-card__icon--warn">🖼️</div>
                    <div><div className="cpiw-stat-card__value">{badges.filter(b => b.badge_define === 'or_image_badge').length}</div><div className="cpiw-stat-card__label">Image Badges</div></div>
                </div>
            </div>

            <div className="cpiw-card">
                <div className="cpiw-card__header">
                    <div className="cpiw-card__header-icon"><span style={{ fontSize: 16 }}>🏷️</span></div>
                    <div style={{ flex: 1 }}>
                        <div className="cpiw-card__title">All Badges</div>
                        <div className="cpiw-card__desc">Click Edit to modify a badge, or create a new one.</div>
                    </div>
                    <button className="cpiw-btn" onClick={onNew}>+ New Badge</button>
                </div>

                <div className="cpiw-card__body">
                    <div className="cpiw-table-wrap">
                        <table className="cpiw-table">
                            <thead>
                                <tr>
                                    <th>Preview</th>
                                    <th>Badge Name</th>
                                    <th>Type</th>
                                    <th>Position</th>
                                    <th>Condition</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {badges.length > 0 ? badges.map(badge => (
                                    <tr key={badge.id}>
                                        <td style={{ width: 80 }}>{getPreview(badge)}</td>
                                        <td style={{ fontWeight: 600 }}>{badge.title || '—'}</td>
                                        <td>
                                            <span className={`cpiw-badge ${badge.badge_define === 'or_image_badge' ? 'cpiw-badge--no' : 'cpiw-badge--yes'}`}>
                                                {badge.badge_define === 'or_image_badge' ? '🖼 Image' : '🔤 Text'}
                                            </span>
                                        </td>
                                        <td style={{ textTransform: 'capitalize', fontSize: 12.5 }}>
                                            {(badge.pbfw_image_position || 'top_left').replace(/_/g, ' ')}
                                        </td>
                                        <td style={{ fontSize: 12.5 }}>
                                            {(badge.pbfw_pro_condition || 'all_products').replace(/_/g, ' ')}
                                        </td>
                                        <td>
                                            {badge.pbfw_show_label === 'on'
                                                ? <span className="cpiw-badge cpiw-badge--yes">✓ Active</span>
                                                : <span className="cpiw-badge cpiw-badge--no">✕ Hidden</span>
                                            }
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button className="cpiw-btn cpiw-btn--sm" onClick={() => onEdit(badge.id)}>✎ Edit</button>
                                                <button className="cpiw-btn-danger cpiw-btn--sm" onClick={() => onDelete(badge.id)}>🗑</button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr className="cpiw-empty-row">
                                        <td colSpan="7">
                                            <span className="cpiw-empty-icon">🏷️</span>
                                            No badges yet. <button className="cpiw-btn cpiw-btn--sm" onClick={onNew} style={{ marginLeft: 8 }}>Create your first badge</button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BadgeList;
